import React, { useEffect, useMemo, useState } from "react"

import { AlertCircle, Plus, X } from "lucide-react"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { MultiSelect } from "@/components/ui/multi-select"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { Textarea } from "@/components/ui/textarea"
import { ukToday } from "@/constants/sheriffOptions"
import { useDepots } from "@/hooks/useDepots"
import { useJobPostings } from "@/hooks/useJobPostings"
import { useSubTeams } from "@/hooks/useSubTeams"
import { JobPosting, JobPostingStage } from "@/types/jobPosting"
import { CreateJobPostingDTO, UpdateJobPostingDTO } from "@/types/jobPosting"
import { deactivatedLabel, depotPickerOptions } from "@/utils/depotValidity"
import { createJobIdAndUrl } from "@/utils/jobUrlUtils"

import { stagesConfig as kanbanStagesConfig } from "./KanbanStageConfig"
import StagesList from "./StagesList"
import type { Stage as FormStage } from "./StagesList"

interface JobPostingFormProps {
  onClose: () => void
  initialData?: JobPosting
}

const SHIFT_TYPES = [
  { value: "HivedVan", label: "Hived Van" },
  { value: "HivedHgv", label: "Hived HGV" },
  { value: "TakeHomeVan", label: "Take Home Van" },
  { value: "Dsp", label: "DSP" },
  { value: "AssociateDriver", label: "Associate Driver" },
] as const

// Reuse unified stages from Kanban and filter out terminal states for the form.
// Also map to StagesList.Stage shape (ensuring required and segments defaults).
const formStagesConfig: FormStage[] = kanbanStagesConfig
  .filter((s) => !["complete", "rejected", "hold", "pending"].includes(s.id))
  .map((s) => ({
    id: s.id,
    label: s.label,
    required: !!s.required,
    icon: s.icon,
    color: s.color,
    segments: s.segments ?? [],
    meta: s.meta,
  }))

const DEFAULT_PAY_RATE = "14.56"

const isHivedOrAgency = (employer: string) => employer === "HIVED" || employer === "Agency"

export const JobPostingForm: React.FC<JobPostingFormProps> = ({ onClose, initialData }) => {
  const { createJobPosting, updateJobPosting } = useJobPostings()
  const { data: depots, isLoading: depotsLoading } = useDepots()
  const { subTeamOptionsWithIds, loading: subTeamsLoading } = useSubTeams()
  const isEditing = !!initialData
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    type: "",
    shiftAreas: [] as string[],
    shiftType: null,
    employer: "", // New field for employer type
    dspId: null,
    subTeamId: null,
    dsp: "", // Text field for DSP name
    driverManagerTeam: "", // Text field for driver manager team name
    location: "",
    vehicleType: null,
    customUrl: "",
    sjtPassMark: "",
    orientationPassMark: "",
    driverOnroadTestPassMark: "",
    salary: "",
    dspContactEmails: [""] as string[], // Array for DSP contact emails (will be joined before submission)
    dataCollectionOnly: false, // New field for data collection jobs
    payBasis: "",
    payGroup: "",
    payRate: DEFAULT_PAY_RATE,
  })

  // Every depot, with closed ones last and disabled (labelled with when they closed)
  const depotOptions = useMemo(() => depotPickerOptions(depots ?? [], ukToday()), [depots])

  // Available shift areas options
  const shiftAreasOptions = ["Rc1 Deliveries", "Rc2 Deliveries", "Collections", "Sortation"]

  // Set initial stages based on initialData or default to basic-details only
  const [selectedStages, setSelectedStages] = useState<JobPostingStage[]>(() => {
    const _initialStages = [
      {
        id: "basic-details",
        segments: [{ id: "contact-details" }, { id: "dob", meta: { ageLimit: 18 } }],
      },
    ]

    return _initialStages
  })

  // Validation dialog state
  const [showValidationDialog, setShowValidationDialog] = useState(false)
  const [invalidStages, setInvalidStages] = useState<
    Array<{ id: string; label: string; icon: React.ElementType; color: string }>
  >([])

  // Set initial data when editing
  useEffect(() => {
    if (initialData) {
      setFormData({
        title: initialData.title || "",
        description: initialData.description || "",
        type: initialData.person_type || "",
        shiftAreas: initialData.shift_areas || [], // Now handling as an array
        shiftType: initialData.sheriff_type || "",
        employer: initialData.employer || "", // Load employer if available
        dspId: initialData.dsp_id || "",
        subTeamId: initialData.driver_manager_team_id || "",
        dsp: initialData.dsp || "",
        driverManagerTeam: initialData.driver_manager_team || "",
        location: initialData.depot_id || "",
        vehicleType: initialData.vehicle_type || "",
        customUrl: initialData.url || "",
        sjtPassMark: initialData.pass_mark_sjt || "",
        orientationPassMark: initialData.pass_mark_np || "",
        driverOnroadTestPassMark: initialData.pass_mark_driver_onroad_test?.toString() || "",
        salary: initialData.salary || "",
        dspContactEmails: initialData.dsp_contact_emails
          ? initialData.dsp_contact_emails.split(",").map((email) => email.trim())
          : [""], // Load DSP contact emails if available
        dataCollectionOnly: initialData.data_collection_only || false,
        payBasis: initialData.pay_basis || "",
        payGroup: initialData.pay_group || "",
        payRate: initialData.pay_rate?.toString() || DEFAULT_PAY_RATE,
      })

      setSelectedStages(initialData.stages)
    } else {
      resetForm()
    }
  }, [initialData])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  // Handle DSP contact email changes
  const handleDspContactEmailChange = (index: number, value: string) => {
    setFormData((prev) => {
      const newEmails = [...prev.dspContactEmails]
      newEmails[index] = value
      return {
        ...prev,
        dspContactEmails: newEmails,
      }
    })
  }

  // Add a new email input field
  const addDspContactEmail = () => {
    setFormData((prev) => ({
      ...prev,
      dspContactEmails: [...prev.dspContactEmails, ""],
    }))
  }

  // Remove an email input field
  const removeDspContactEmail = (index: number) => {
    setFormData((prev) => {
      const newEmails = [...prev.dspContactEmails]
      newEmails.splice(index, 1)
      // Always keep at least one field
      if (newEmails.length === 0) {
        newEmails.push("")
      }
      return {
        ...prev,
        dspContactEmails: newEmails,
      }
    })
  }
  const handleSelectChange = (name: string, value: string) => {
    if (value) {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }))
    }
  }

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      type: "",
      shiftAreas: [],
      shiftType: "",
      employer: "",
      dspId: "",
      subTeamId: "",
      dsp: "",
      driverManagerTeam: "",
      location: "",
      vehicleType: "",
      customUrl: "",
      sjtPassMark: "",
      orientationPassMark: "",
      driverOnroadTestPassMark: "",
      salary: "",
      dspContactEmails: [""],
      dataCollectionOnly: false,
      payBasis: "",
      payGroup: "",
      payRate: DEFAULT_PAY_RATE,
    })
    setSelectedStages([
      {
        id: "basic-details",
        segments: [{ id: "contact-details" }, { id: "dob", meta: { ageLimit: 18 } }],
      },
    ])
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      // Validate stages with segments
      const stagesWithSegments = formStagesConfig.filter(
        (stage) => stage.segments && stage.segments.length > 0,
      )

      const stagesWithoutSegments = selectedStages.filter((selectedStage) => {
        const stageConfig = stagesWithSegments.find((s) => s.id === selectedStage.id)
        // If this stage has segments available but none are selected
        return stageConfig && (!selectedStage.segments || selectedStage.segments.length === 0)
      })

      if (stagesWithoutSegments.length > 0) {
        const stagesInfo = stagesWithoutSegments.map((stage) => {
          const config = formStagesConfig.find((s) => s.id === stage.id)
          return {
            id: stage.id,
            label: config?.label || stage.id,
            icon: config?.icon || AlertCircle,
            color: config?.color || "#6B7280",
          }
        })

        setInvalidStages(stagesInfo)
        setShowValidationDialog(true)
        return
      }

      // Filter out empty email fields and join into comma-separated string
      const filteredEmails = formData.dspContactEmails.filter((email) => email.trim() !== "")
      const dspContactEmailsString = filteredEmails.join(",")

      // For new job postings, generate a UUID and URL
      const { jobId, jobUrl } = !isEditing ? createJobIdAndUrl() : { jobId: "", jobUrl: "" }

      // Prepare data for submission
      // For data collection jobs, set all non-essential fields to null
      const jobPostingData = {
        title: formData.title,
        description: formData.description,
        person_type: formData.dataCollectionOnly ? null : formData.type,
        shift_areas: formData.dataCollectionOnly ? null : formData.shiftAreas,
        sheriff_type: formData.dataCollectionOnly ? null : formData.shiftType,
        employer: formData.dataCollectionOnly ? null : formData.employer,
        dsp_id:
          formData.dataCollectionOnly ||
          (formData.employer !== "DSP" && formData.employer !== "Agency")
            ? null
            : formData.dspId,
        driver_manager_team_id: formData.dataCollectionOnly ? null : formData.subTeamId || null,
        dsp:
          formData.dataCollectionOnly ||
          (formData.employer !== "DSP" && formData.employer !== "Agency")
            ? null
            : formData.dsp || null,
        driver_manager_team: formData.dataCollectionOnly
          ? null
          : formData.driverManagerTeam || null,
        depot_id: formData.dataCollectionOnly ? null : formData.location || null,
        vehicle_type: formData.dataCollectionOnly ? null : formData.vehicleType,
        url: isEditing ? initialData?.url : jobUrl, // Use generated URL for new jobs, keep existing URL when editing
        pass_mark_sjt: formData.sjtPassMark || null,
        pass_mark_np: formData.orientationPassMark || null,
        pass_mark_driver_onroad_test: formData.driverOnroadTestPassMark
          ? Number(formData.driverOnroadTestPassMark)
          : null,
        salary: formData.dataCollectionOnly ? null : formData.salary,
        dsp_contact_emails:
          formData.dataCollectionOnly ||
          (formData.employer !== "DSP" && formData.employer !== "Agency")
            ? undefined
            : filteredEmails.length > 0
              ? dspContactEmailsString
              : undefined,
        pay_basis:
          formData.dataCollectionOnly || !isHivedOrAgency(formData.employer)
            ? null
            : formData.payBasis || null,
        pay_group:
          formData.dataCollectionOnly || !isHivedOrAgency(formData.employer)
            ? null
            : formData.payGroup || null,
        pay_rate:
          formData.dataCollectionOnly || !isHivedOrAgency(formData.employer)
            ? null
            : parseFloat(formData.payRate) > 0
              ? parseFloat(formData.payRate)
              : null,
        stages: selectedStages,
        active: true,
        hidden: isEditing ? (initialData?.hidden ?? false) : true, // New jobs hidden by default, editing preserves state
        data_collection_only: formData.dataCollectionOnly,
      }

      // If creating a new job, add the ID
      if (!isEditing) {
        // @ts-expect-error - We're setting the ID manually for new jobs
        jobPostingData.id = jobId
      }

      if (isEditing && initialData) {
        updateJobPosting({
          id: initialData.id,
          data: jobPostingData as unknown as UpdateJobPostingDTO,
        })
      } else {
        createJobPosting(jobPostingData as unknown as CreateJobPostingDTO)
      }
      resetForm()
      onClose()
    } catch (error) {
      console.error("Failed to save job posting:", error)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4 mt-4">
      <div className="grid gap-4">
        <div className="grid grid-cols-1 gap-2">
          <Label htmlFor="title">Job Title</Label>
          <Input
            id="title"
            name="title"
            value={formData.title}
            onChange={handleChange}
            placeholder="Enter job title"
            required
          />
        </div>

        <div className="grid grid-cols-1 gap-2">
          <Label htmlFor="description">Job Description</Label>
          <Textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="Enter job description"
            required
            className="min-h-[100px]"
          />
        </div>

        <div className="flex items-center justify-between p-4 bg-amber-50 border border-amber-200 rounded-md">
          <div className="flex-1">
            <Label htmlFor="dataCollectionOnly" className="cursor-pointer">
              <span className="font-semibold text-base">Data Collection Only</span>
              <p className="text-xs text-muted-foreground mt-1 font-normal">
                Use this job to collect information from existing drivers (skips Airtable sync and
                hiring workflows)
              </p>
            </Label>
          </div>
          <Switch
            id="dataCollectionOnly"
            checked={formData.dataCollectionOnly}
            onCheckedChange={(checked) =>
              setFormData((prev) => ({
                ...prev,
                dataCollectionOnly: checked,
              }))
            }
          />
        </div>

        {/* Hide all these fields when data collection only is checked */}
        {!formData.dataCollectionOnly && (
          <>
            <div className="grid grid-cols-1 gap-2">
              <Label htmlFor="type">Person Type</Label>
              <Select
                value={formData.type}
                onValueChange={(value) => handleSelectChange("type", value)}
                required
              >
                <SelectTrigger id="type">
                  <SelectValue placeholder="Select person type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Driver">Driver</SelectItem>
                  <SelectItem value="Warehouse">Warehouse</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 gap-2">
              <Label>Block Areas</Label>
              <MultiSelect
                options={shiftAreasOptions.map((area) => ({
                  label: area,
                  value: area,
                }))}
                selected={formData.shiftAreas || []}
                onChange={(selected) =>
                  setFormData((prev) => ({
                    ...prev,
                    shiftAreas: selected || [],
                  }))
                }
                placeholder="Select block areas"
                emptyMessage="No block areas found."
              />
            </div>

            <div className="grid grid-cols-1 gap-2">
              <Label htmlFor="shiftType">Block Type</Label>
              <Select
                value={formData.shiftType}
                onValueChange={(value) => handleSelectChange("shiftType", value)}
                required
              >
                <SelectTrigger id="shiftType">
                  <SelectValue placeholder="Select block type" />
                </SelectTrigger>
                <SelectContent>
                  {SHIFT_TYPES.map((v) => (
                    <SelectItem key={v.value} value={v.value}>
                      {v.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 gap-2">
              <Label htmlFor="employer">Employer</Label>
              <Select
                value={formData.employer}
                onValueChange={(value) => handleSelectChange("employer", value)}
                required
              >
                <SelectTrigger id="employer">
                  <SelectValue placeholder="Select employer type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="HIVED">HIVED</SelectItem>
                  <SelectItem value="Self Employed">Self Employed</SelectItem>
                  <SelectItem value="DSP">DSP</SelectItem>
                  <SelectItem value="Not Employed">Not Employed</SelectItem>
                  <SelectItem value="Agency">Agency</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {isHivedOrAgency(formData.employer) && (
              <div className="p-4 border border-palette-jungleGreen200 rounded-md bg-palette-jungleGreen000 space-y-4">
                <div className="grid grid-cols-1 gap-2">
                  <Label htmlFor="payBasis">Pay Basis</Label>
                  <Select
                    value={formData.payBasis}
                    onValueChange={(value) => handleSelectChange("payBasis", value)}
                  >
                    <SelectTrigger id="payBasis">
                      <SelectValue placeholder="Select pay basis" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Hourly">Hourly</SelectItem>
                      <SelectItem value="Daily">Daily</SelectItem>
                      <SelectItem value="Monthly">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-1 gap-2">
                  <Label htmlFor="payGroup">Pay Group</Label>
                  <Select
                    value={formData.payGroup}
                    onValueChange={(value) => handleSelectChange("payGroup", value)}
                  >
                    <SelectTrigger id="payGroup">
                      <SelectValue placeholder="Select pay group" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Fortnightly">Fortnightly</SelectItem>
                      <SelectItem value="Monthly">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-1 gap-2">
                  <Label htmlFor="payRate">Pay Rate (£)</Label>
                  <Input
                    id="payRate"
                    name="payRate"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.payRate}
                    onChange={handleChange}
                    placeholder="Enter pay rate"
                  />
                </div>
              </div>
            )}

            {(formData.employer === "DSP" || formData.employer === "Agency") && (
              <div className="p-4 border border-palette-jungleGreen200 rounded-md bg-palette-jungleGreen000 space-y-4">
                <div className="grid grid-cols-1 gap-2">
                  <Label htmlFor="dspId">DSP</Label>
                  <Select
                    value={formData.dspId || undefined}
                    onValueChange={(value) => {
                      handleSelectChange("dspId", value)
                      // Find the selected DSP and update the dsp text field with the label
                      const selectedDsp = subTeamOptionsWithIds.find(
                        (option) => option.type === "DSP" && option.id === value,
                      )

                      if (selectedDsp) {
                        setFormData((prev) => ({
                          ...prev,
                          dsp: selectedDsp.label,
                          dspId: selectedDsp.id,
                        }))
                      }
                    }}
                    disabled={subTeamsLoading}
                    required
                  >
                    <SelectTrigger id="dspId">
                      <SelectValue placeholder="Select a DSP" />
                    </SelectTrigger>
                    <SelectContent>
                      {subTeamOptionsWithIds
                        .filter((option) => option.type === "DSP")
                        .map((dsp) => (
                          <SelectItem key={dsp.id} value={dsp.id}>
                            {dsp.label}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* DSP Contact Emails - only visible when DSP is selected */}
                {formData.dspId != null && (
                  <div className="grid grid-cols-1 gap-2">
                    <Label htmlFor="dspContactEmails">DSP Contact Emails</Label>
                    <div className="space-y-2">
                      {formData.dspContactEmails.map((email, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <Input
                            id={`dspContactEmail-${index}`}
                            value={email}
                            onChange={(e) => handleDspContactEmailChange(index, e.target.value)}
                            placeholder="Enter email address"
                            type="email"
                            className="flex-1"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            onClick={() => removeDspContactEmail(index)}
                            className="h-10 w-10"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={addDspContactEmail}
                        className="flex items-center gap-1"
                      >
                        <Plus className="h-4 w-4" /> Add Email
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 gap-2">
              <Label htmlFor="subTeamId">Sub Team</Label>
              <Select
                value={formData.subTeamId || "none"}
                onValueChange={(value) => {
                  if (value === "none") {
                    setFormData((prev) => ({ ...prev, subTeamId: null, driverManagerTeam: "" }))
                    return
                  }
                  handleSelectChange("subTeamId", value)
                  // Find the selected team and update the driver_manager_team text field with the label
                  const selectedTeam = subTeamOptionsWithIds.find(
                    (option) => option.type === "DRIVER_MANAGER_TEAM" && option.id === value,
                  )
                  if (selectedTeam) {
                    setFormData((prev) => ({
                      ...prev,
                      driverManagerTeam: selectedTeam.label,
                      subTeamId: selectedTeam.id,
                    }))
                  }
                }}
                disabled={subTeamsLoading}
              >
                <SelectTrigger id="subTeamId">
                  <SelectValue
                    placeholder={subTeamsLoading ? "Loading teams..." : "Select Sub Team"}
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {subTeamOptionsWithIds
                    .filter((option) => option.type === "DRIVER_MANAGER_TEAM")
                    .map((team) => (
                      <SelectItem key={team.id} value={team.id}>
                        {team.label}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 gap-2">
              <Label htmlFor="location">Location</Label>
              <Select
                value={formData.location}
                onValueChange={(value) => handleSelectChange("location", value)}
                required
                disabled={depotsLoading}
              >
                <SelectTrigger id="location">
                  <SelectValue
                    placeholder={depotsLoading ? "Loading depots..." : "Select location"}
                  />
                </SelectTrigger>
                <SelectContent>
                  {depotOptions.map(({ depot, closedOn }) => (
                    <SelectItem key={depot.id} value={depot.id} disabled={Boolean(closedOn)}>
                      {depot.name}({depot.depot_id}){closedOn && ` ${deactivatedLabel(closedOn)}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 gap-2">
              <Label htmlFor="vehicleType">Vehicle Type</Label>
              <Select
                value={formData.vehicleType}
                onValueChange={(value) => handleSelectChange("vehicleType", value)}
                required
              >
                <SelectTrigger id="vehicleType">
                  <SelectValue placeholder="Select vehicle type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Hived Vehicle">Hived Vehicle</SelectItem>
                  <SelectItem value="Own Vehicle">Own Vehicle</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 gap-2">
              <Label htmlFor="salary">Salary</Label>
              <Input
                id="salary"
                name="salary"
                value={formData.salary}
                onChange={handleChange}
                placeholder="Enter salary information"
              />
            </div>
          </>
        )}

        <div className="grid grid-cols-1 gap-2 pt-2">
          <StagesList
            stages={formStagesConfig}
            selectedStages={selectedStages}
            setSelectedStages={setSelectedStages}
          />
        </div>
      </div>

      <div className="flex justify-end space-x-3 pt-4">
        <Button
          variant="outline"
          type="button"
          onClick={() => {
            resetForm()
            onClose()
          }}
        >
          Cancel
        </Button>
        <Button type="submit">{isEditing ? "Update Job Posting" : "Create Job Posting"}</Button>
      </div>

      {/* Validation Dialog */}
      <Dialog open={showValidationDialog} onOpenChange={setShowValidationDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertCircle className="h-5 w-5" />
              Missing Segments
            </DialogTitle>
            <DialogDescription>
              The following stages require at least one segment to be selected:
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-4">
            {invalidStages.map((stage) => {
              const Icon = stage.icon
              return (
                <div
                  key={stage.id}
                  className="flex items-center gap-3 p-3 rounded-lg border bg-palette-jungleGreen000"
                >
                  <div
                    className="flex items-center justify-center w-10 h-10 rounded-full"
                    style={{ backgroundColor: `${stage.color}20` }}
                  >
                    <Icon className="w-5 h-5" style={{ color: stage.color }} />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-palette-jungleGreen900">{stage.label}</p>
                    <p className="text-sm text-palette-jungleGreen500">
                      Please select at least one segment for this stage
                    </p>
                  </div>
                </div>
              )
            })}
          </div>

          <DialogFooter>
            <Button type="button" onClick={() => setShowValidationDialog(false)} className="w-full">
              OK, I'll add segments
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </form>
  )
}
